import React, { useCallback, useEffect, useState } from "react";
import { ConfirmModal } from "../common/ConfirmModal";
import { User, Tournament, Season, EventLocation, TournamentEvent } from "../../types";
import { Button } from "../common/Button";
import { TextField } from "../common/form/FormField";

type CrudModels = User | Tournament | Season | EventLocation | TournamentEvent;
type Field<T extends CrudModels> =  { 
    name: keyof T; 
    label: string; 
    type: "text" | "date" | "number" | "checkbox"; 
    required?: boolean;
    defaultValue?: string | number | boolean;
  };

interface CrudConfig<T extends CrudModels> {
  title: string;
  elementName: string;
  getItems: () => Promise<T[]>;
  createItem: (data: Partial<T>) => Promise<T>;
  updateItem: (id: number, data: Partial<T>) => Promise<T>;
  deleteItem: (id: number) => Promise<void>;
  renderItem: (item: T) => React.ReactNode;
  getTextField: (item: T) => string;
  fields: Field<T>[];
}

interface CrudProps<T extends CrudModels> {
  config: CrudConfig<T>;
}

export function BasePanel<T extends CrudModels>({ config }: CrudProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [modal, setModal] = useState<{ mode: "create" | "edit"; item?: T } | null>(null);
  const [error, setError] = useState("");
  const [txtFilter, setTxtFilter] = useState("");
  const [message, setMessage] = useState("");

  const fetchItems = useCallback(async () => {
    const data = await config.getItems();
    data.sort((a, b) => {
      let a_name = config.getTextField(a);
      let b_name = config.getTextField(b);
      return a_name.localeCompare(b_name);
    });
    setItems(data);
  }, [config]);

  // Carica lista
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSave = async (formData: Partial<T>) => {
    setError("");
    try {

      if (modal?.mode === "edit" && modal.item) {
        // Update existing season
        await config.updateItem(modal.item.id, formData)
      } else {
        // Create new season
        await config.createItem(formData)
      }
      fetchItems()
      setModal(null)
      setMessage("Salvataggio avvenuto con successo")
    } catch (err: any) {
      setError(err.message);
    }
  }

  const handleDelete = async (id: number) => {
    await config.deleteItem(id)
    fetchItems()
  }

  return (
    <div>
      <div className="flex justify-between mb-3">
        <h2 className="text-xl font-semibold">{config.title} ({items.length})</h2>
        <Button
          variant="primary"
          onClick={() => setModal({ mode: "create" })}
        >
          + Nuovo
        </Button>
      </div>
      <div className="mb-3 text-right">
        Filtra: <input type="text" className="border border-gray-300 px-2 py-1 rounded" onChange={(e) => setTxtFilter(e.target.value)} />
      </div>
      {message && <div className="text-green-600 text-sm mb-4">{message}</div>}
      <ul className="space-y-2">
        {items.map((i) => (
          <li
            key={i.id}
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-white shadow-md rounded p-3 mb-2"
            hidden={txtFilter.length > 0 && !config.getTextField(i).toLowerCase().includes(txtFilter.toLowerCase())}
          >
            {/* Blocco informazioni */}
            { config.renderItem(i) }

            {/* Bottoni */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="warning"
                onClick={() => setModal({ mode: "edit", item: i })}
                size="sm"
              >
                Modifica
              </Button>
              <ConfirmModal
                message={`Sei sicuro di voler eliminare questo elemento?`}
                onConfirm={() => handleDelete(i.id)}
                confirmText="Elimina"
                cancelText="Annulla"
                size="sm"
              />
            </div>
          </li>

        ))}
      </ul>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex justify-center items-end bg-black/50">
          <div className="bg-white rounded-t-xl w-full max-w-md p-6 animate-slideUp">
            <h3 className="text-lg font-bold mb-4">
              {modal.mode === "create" ? "Crea" : "Modifica"} {config.elementName}
            </h3>
            <ModalForm<T> 
              modal={modal} 
              onSave={handleSave} 
              onCancel={() => setModal(null)} 
              fields={config.fields}
              error={error}
              />
          </div>
        </div>
      )}
    </div>
  )
}

function ModalForm<T extends CrudModels>({
  modal,
  fields,
  onSave,
  onCancel,
  error,
}: {
  modal: { mode: "create" | "edit"; item?: T }
  fields: Field<T>[]
  onSave: (data: Partial<T>) => void
  onCancel: () => void,
  error: string,
}) {

  const renderField = (field: Field<T>) => {
    const name = field.name as string;
    const required = field.required ?? false;
    switch (field.type) {
      case "text":
        return (
          <TextField
            label={field.label}
            type="text"
            name={name}
            required={required}
            defaultValue={modal.item ? (modal.item[field.name] as string) : ((field.defaultValue as string) ?? "")}
          />
        );
      case "number":
        return (
          <TextField
            label={field.label}
            type="number"
            name={name}
            required={required}
            defaultValue={modal.item ? (modal.item[field.name] as number) : ((field.defaultValue as number) ?? 0)}
          />
        );
      case "checkbox":
        return (
          <label className="block">
            <input
              type="checkbox"
              className="form-checkbox rounded  px-2 py-1 mr-2"
              defaultChecked={modal.item ? (modal.item[field.name] as boolean) : ((field.defaultValue as boolean) ?? false)}
              name={name}
            />
            {field.label}
          </label>
        );
      case "date":
        return (
          <TextField
            label={field.label}
            type="date"
            name={name}
            required={required}
            defaultValue={modal.item ? (modal.item[field.name] as string) : ((field.defaultValue as string) ?? "")}
          />
        );
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget as HTMLFormElement);
        const data: Partial<T> = {};
        fields.forEach((f) => {
            if (f.type === "checkbox") {
              data[f.name] = (formData.get(f.name as string) ? true : false) as any;
            } else {
              data[f.name] = formData.get(f.name as string) as any;
            }
        });
        onSave(data);
      }}
      className="space-y-3"
    >
      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
      {fields.map((field) => renderField(field))}
      <div className="flex justify-end space-x-2">
        <Button variant="secondary" onClick={onCancel}>
          Annulla
        </Button>
        <Button className="px-3 py-1 bg-blue-600 text-white rounded">Salva</Button>
      </div>
    </form>
  )
}
